import React from 'react';
import { useAutoExchange } from '@/features/onboarding/hooks/use-auto-exchange';
import type { FactorType } from '@/features/onboarding/types/onboarding.types';
import { CompleteStepError } from '../complete-step/complete-step-error';
import { CompleteStepExchanging } from '../complete-step/complete-step-exchanging';
import { TenantCompleteStepSuccess } from './tenant-complete-step-success';

interface TenantCompleteStepProps {
  enrolledFactors: FactorType[];
  userName?: string;
  isExchanging: boolean;
  isAuthenticated: boolean;
  exchangeError: string | null;
  onExchange: () => Promise<boolean>;
  onComplete: () => void | Promise<void>;
}

/**
 * Tenant Complete Step - Final step of tenant onboarding wizard
 *
 * Flow:
 * 1. Auto-triggers JWT exchange on mount
 * 2. User clicks button to redirect to admin-console/dashboard
 *
 * NOTE: Tenant activation is handled by the backend during onboarding completion.
 * Reuses error and exchanging states from CompleteStep.
 * Has its own success state with tenant-specific content.
 */
export const TenantCompleteStep: React.FC<TenantCompleteStepProps> = ({
  enrolledFactors,
  userName,
  isExchanging,
  isAuthenticated,
  exchangeError,
  onExchange,
  onComplete,
}) => {
  // Auto-trigger JWT exchange when component mounts
  useAutoExchange({
    isAuthenticated,
    isExchanging,
    exchangeError,
    onExchange,
  });

  // Show loading while exchanging JWT
  if (isExchanging) {
    return <CompleteStepExchanging />;
  }

  // Show error if exchange failed
  if (exchangeError) {
    return <CompleteStepError error={exchangeError} onRetry={onExchange} />;
  }

  // Show loading until authenticated
  if (!isAuthenticated) {
    return <CompleteStepExchanging />;
  }

  // Show success - user can now redirect to admin console
  return (
    <TenantCompleteStepSuccess
      userName={userName}
      enrolledFactors={enrolledFactors}
      onComplete={onComplete}
    />
  );
};
